import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

/**
 * RequestDemo — expanded qualification form. Posts through the existing
 * /api/leads pipeline (CRM webhook + Resend automated whitepaper), plus
 * the new BioHacker Sports qualification fields the sales team asked for.
 */

const ORG_TYPES = [
  "Individual Athlete", "Team", "Club Organization", "High School",
  "Athletic Department", "NCAA", "NAIA", "JUCO", "Professional Organization",
  "Sports Medicine", "Private Performance Facility", "Military", "Other",
];

const OBJECTIVES = [
  "Performance", "Injury Prevention", "Recovery", "Workload",
  "Recruiting", "Department Analytics", "Return-to-Play", "Athlete Development",
];

const SPORTS_LIST = [
  "Baseball", "Softball", "Football", "Basketball", "Soccer",
  "Volleyball", "Track & Field", "Olympic Sport", "Multi-sport", "Other",
];

export default function RequestDemo() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", org_name: "",
    org_type: "", primary_sport: "", num_athletes: "", num_teams: "",
    current_tech: "", primary_objective: "", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Please fill in name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        organization: form.org_name || undefined,
        source: "biohacker_request_demo",
        segment: form.org_type || undefined,
        // Enrich the lead with the new qualification payload so CRM
        // routing + follow-up personalization has the full context.
        metadata: {
          primary_sport: form.primary_sport,
          num_athletes: form.num_athletes,
          num_teams: form.num_teams,
          current_tech_stack: form.current_tech,
          primary_objective: form.primary_objective,
          notes: form.notes,
        },
      });
      setSubmitted(true);
    } catch (err) {
      // Backend can return a Pydantic-shaped 422 whose `detail` is an
      // ARRAY of dicts — passing that straight to toast.error / React
      // crashes the reconciler. Coerce to a string before rendering.
      const d = err?.response?.data?.detail;
      let msg = "Could not submit request";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((e) => e?.msg || e?.type || "invalid input").join("; ");
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050810] text-white" data-testid="request-demo-page">
        <SiteNav />
        <section className="max-w-2xl mx-auto px-6 pt-40 pb-24 text-center" data-testid="request-demo-success">
          <div className="h-14 w-14 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center mx-auto">
            <Check className="h-7 w-7 text-emerald-300" />
          </div>
          <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl text-white tracking-tight">You&apos;re in.</h1>
          <p className="mt-4 text-white/70 leading-relaxed">
            Our team will reach out within one business day with a personalized walkthrough
            tuned to your organization type, sport, and primary objective.
          </p>
          <p className="mt-3 text-[11px] font-mono-num text-white/40">
            A copy of the BioHacker Sports™ Capabilities Report is on its way to your inbox.
          </p>
          <div className="mt-8">
            <Link to="/">
              <Button className="text-white font-bold text-sm" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Back to home <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="request-demo-page">
      <SiteNav />
      <section className="pt-32 pb-16 max-w-3xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Request Demo</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight leading-[1.05]">
          Get a walkthrough<br/>
          <span className="text-white/50">tailored to your organization.</span>
        </h1>
        <p className="mt-4 text-white/60 max-w-xl">
          Tell us a bit about your program and we&apos;ll personalize the entire demo — the sport, the audience,
          and the workflows. You&apos;ll also receive our latest Capabilities Report by email.
        </p>

        <form onSubmit={submit} className="mt-10 ink-card rounded-2xl p-6 space-y-5" data-testid="request-demo-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="demo-name" />
            <Field label="Work email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required type="email" testid="demo-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="demo-phone" />
            <Field label="Organization" value={form.org_name} onChange={(v) => setForm({ ...form, org_name: v })} testid="demo-org" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Select label="Organization type" value={form.org_type} onChange={(v) => setForm({ ...form, org_type: v })}
              options={ORG_TYPES} testid="demo-org-type" />
            <Select label="Primary sport" value={form.primary_sport} onChange={(v) => setForm({ ...form, primary_sport: v })}
              options={SPORTS_LIST} testid="demo-sport" />
            <Field label="Number of athletes" value={form.num_athletes} onChange={(v) => setForm({ ...form, num_athletes: v })} testid="demo-num-athletes" />
            <Field label="Number of teams" value={form.num_teams} onChange={(v) => setForm({ ...form, num_teams: v })} testid="demo-num-teams" />
          </div>
          <Field label="Current technology stack (FlightScope, VALD, WHOOP, etc.)"
            value={form.current_tech} onChange={(v) => setForm({ ...form, current_tech: v })} testid="demo-tech-stack" />
          <Select label="Primary objective" value={form.primary_objective} onChange={(v) => setForm({ ...form, primary_objective: v })}
            options={OBJECTIVES} testid="demo-objective" />
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Anything else we should know?</Label>
            <textarea
              className="mt-1 w-full rounded-md bg-white/[0.04] border border-white/12 text-white/90 text-sm p-3 focus:outline-none focus:border-white/30"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="demo-notes"
            />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full text-white font-bold text-sm py-6"
            style={{ backgroundColor: "var(--brand-secondary)" }}
            data-testid="demo-submit">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                   : <>Request my personalized demo <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </section>
      <SiteFooter />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">{label}{required && " *"}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="mt-1 bg-white/[0.04] border-white/12 text-white/90"
        data-testid={testid} />
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md bg-white/[0.04] border border-white/12 text-white/90 text-sm py-2 px-3 focus:outline-none focus:border-white/30"
        data-testid={testid}>
        <option value="">— Select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
